import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { statsService } from '../services/api';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
`;

const Card = styled.div<{ accent?: string }>`
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  border-left: 4px solid ${p => p.accent || '#3b82f6'};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardIcon = styled.div`
  font-size: 22px;
  margin-bottom: 4px;
`;

const CardValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
`;

const CardLabel = styled.div`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

const CardBadge = styled.div<{ color?: string }>`
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  background: ${p => p.color || '#eff6ff'};
  color: ${p => p.color ? 'white' : '#1d4ed8'};
  padding: 2px 8px;
  border-radius: 99px;
  margin-top: 4px;
  width: fit-content;
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LoginRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-bottom: 1px solid #f9fafb;
  &:last-child { border-bottom: none; }
`;

const LoginUser = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div<{ role: string }>`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${p => p.role === 'cook' ? '#fef3c7' : '#eff6ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const LoginName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const LoginRole = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-transform: capitalize;
`;

const LoginTime = styled.div`
  font-size: 12px;
  color: #9ca3af;
`;

const QuickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  padding: 16px 24px;
`;

const QuickBtn = styled.button`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 12px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  &:hover { background: #f3f4f6; border-color: #d1d5db; transform: translateY(-1px); }
`;

const QuickIcon = styled.div`font-size: 20px; margin-bottom: 6px;`;
const QuickLabel = styled.div`font-size: 13px; font-weight: 600; color: #1f2937;`;

const EmptyLogins = styled.div`
  padding: 24px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
`;

interface DashboardStats {
  active_menus: number;
  active_popup: string | null;
  off_menu_products: number;
  recent_logins: { username: string; role: string; last_login: string }[];
}

const formatTime = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);
  if (diffMin < 1) return 'adesso';
  if (diffMin < 60) return `${diffMin} min fa`;
  if (diffH < 24) return `${diffH}h fa`;
  if (diffD === 1) return 'ieri';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
};

const quickActions = [
  { icon: '🪑', label: 'Ordini tavoli', path: '/table-order' },
  { icon: '👨‍🍳', label: 'Vista cucina', path: '/kitchen' },
  { icon: '🏠', label: 'Sale & Tavoli', path: '/rooms' },
  { icon: '🍽️', label: 'Prodotti', path: '/products' },
  { icon: '📂', label: 'Categorie', path: '/categories' },
  { icon: '📋', label: 'Menu', path: '/custom-menus' },
  { icon: '📱', label: 'QR Code', path: '/qr-manager' },
  { icon: '📈', label: 'Analisi', path: '/analytics' },
  { icon: '🏢', label: 'Attività', path: '/business' },
  { icon: '👥', label: 'Utenti', path: '/users' },
  { icon: '🖨️', label: 'Stampa Menu', path: '/menu/stampa' },
];

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    statsService.getStats()
      .then(res => { if (res.success) setStats(res.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Buongiorno' : hour < 18 ? 'Buon pomeriggio' : 'Buonasera';

  return (
    <Container>
      <Header>
        <div>
          <Title>{greeting}, {user?.username}!</Title>
          <Subtitle>{now.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</Subtitle>
        </div>
      </Header>

      <Grid>
        <Card accent="#10b981">
          <CardIcon>📋</CardIcon>
          <CardValue>{loading ? '—' : stats?.active_menus ?? 0}</CardValue>
          <CardLabel>Menu personalizzati attivi</CardLabel>
        </Card>
        <Card accent={stats?.active_popup ? '#f59e0b' : '#e5e7eb'}>
          <CardIcon>💬</CardIcon>
          <CardValue style={{ fontSize: '16px', paddingTop: '6px' }}>
            {loading ? '—' : stats?.active_popup ? stats.active_popup : 'Nessuno'}
          </CardValue>
          <CardLabel>Popup attivo</CardLabel>
          {!loading && stats?.active_popup && <CardBadge color="#f59e0b">In corso</CardBadge>}
        </Card>
      </Grid>

      <Section>
        <SectionHeader>🕐 Ultimi accessi staff</SectionHeader>
        {loading ? (
          <EmptyLogins>Caricamento...</EmptyLogins>
        ) : (stats?.recent_logins?.length ?? 0) === 0 ? (
          <EmptyLogins>Nessun accesso registrato ancora</EmptyLogins>
        ) : (
          stats!.recent_logins.map((u, i) => (
            <LoginRow key={i}>
              <LoginUser>
                <Avatar role={u.role}>
                  {u.role === 'cook' ? '👨‍🍳' : '🧑‍💼'}
                </Avatar>
                <div>
                  <LoginName>{u.username}</LoginName>
                  <LoginRole>{u.role === 'cook' ? 'Cuoco' : 'Cameriere'}</LoginRole>
                </div>
              </LoginUser>
              <LoginTime>{formatTime(u.last_login)}</LoginTime>
            </LoginRow>
          ))
        )}
      </Section>

      <Section>
        <SectionHeader>⚡ Accesso rapido</SectionHeader>
        <QuickGrid>
          {quickActions.map(a => (
            <QuickBtn key={a.path} onClick={() => navigate(a.path)}>
              <QuickIcon>{a.icon}</QuickIcon>
              <QuickLabel>{a.label}</QuickLabel>
            </QuickBtn>
          ))}
        </QuickGrid>
      </Section>
    </Container>
  );
};

export default Dashboard;
